import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useUser } from '@clerk/clerk-react';
import { getSupabase } from '@/lib/supabase';
import type {
  LocalRecommendation,
  CreateRecommendationInput,
  RecommendationCategory,
} from '@/types/database';
import DOMPurify from 'dompurify';

// Category icons and labels - organized by group
export const RECOMMENDATION_CATEGORIES: {
  value: RecommendationCategory;
  label: string;
  labelEn: string;
  icon: string;
  color: string;
  group: 'food' | 'nature' | 'activities' | 'transport' | 'services' | 'events' | 'other';
}[] = [
  // Food & Drink
  { value: 'restaurant', label: 'Restoran', labelEn: 'Restaurant', icon: '🍽️', color: 'bg-orange-500', group: 'food' },
  { value: 'cafe', label: 'Kafić', labelEn: 'Cafe', icon: '☕', color: 'bg-amber-600', group: 'food' },
  { value: 'bar', label: 'Bar', labelEn: 'Bar', icon: '🍸', color: 'bg-purple-500', group: 'food' },
  { value: 'pastry', label: 'Slastičarnica', labelEn: 'Pastry Shop', icon: '🧁', color: 'bg-pink-500', group: 'food' },

  // Nature & Beaches
  { value: 'beach', label: 'Plaža', labelEn: 'Beach', icon: '🏖️', color: 'bg-cyan-500', group: 'nature' },
  { value: 'national_park', label: 'Nacionalni park', labelEn: 'National Park', icon: '🏞️', color: 'bg-green-600', group: 'nature' },
  { value: 'attraction', label: 'Atrakcija', labelEn: 'Attraction', icon: '🎭', color: 'bg-indigo-500', group: 'nature' },
  { value: 'city', label: 'Grad', labelEn: 'City', icon: '🏙️', color: 'bg-slate-500', group: 'nature' },

  // Activities & Sports
  { value: 'excursion', label: 'Izlet', labelEn: 'Excursion', icon: '🚌', color: 'bg-blue-500', group: 'activities' },
  { value: 'hiking', label: 'Pješačenje', labelEn: 'Hiking', icon: '🥾', color: 'bg-green-500', group: 'activities' },
  { value: 'cycling', label: 'Biciklizam', labelEn: 'Cycling', icon: '🚴', color: 'bg-lime-500', group: 'activities' },
  { value: 'diving', label: 'Ronjenje', labelEn: 'Diving', icon: '🤿', color: 'bg-blue-600', group: 'activities' },
  { value: 'water_sports', label: 'Vodeni sportovi', labelEn: 'Water Sports', icon: '🏄', color: 'bg-teal-500', group: 'activities' },
  { value: 'gym', label: 'Teretana', labelEn: 'Gym', icon: '💪', color: 'bg-rose-500', group: 'activities' },
  { value: 'sports_court', label: 'Sportski teren', labelEn: 'Sports Court', icon: '⚽', color: 'bg-emerald-600', group: 'activities' },
  { value: 'playground', label: 'Dječje igralište', labelEn: 'Playground', icon: '🛝', color: 'bg-yellow-500', group: 'activities' },
  { value: 'rental_sea', label: 'Najam - more', labelEn: 'Sea Rental', icon: '🚤', color: 'bg-teal-600', group: 'activities' },
  { value: 'rental_snow', label: 'Najam - snijeg', labelEn: 'Snow Rental', icon: '⛷️', color: 'bg-sky-500', group: 'activities' },

  // Transport
  { value: 'bus', label: 'Autobus', labelEn: 'Bus', icon: '🚌', color: 'bg-yellow-600', group: 'transport' },
  { value: 'taxi', label: 'Taxi', labelEn: 'Taxi', icon: '🚕', color: 'bg-yellow-500', group: 'transport' },
  { value: 'ferry', label: 'Trajekt', labelEn: 'Ferry', icon: '⛴️', color: 'bg-blue-500', group: 'transport' },
  { value: 'airport', label: 'Zračna luka', labelEn: 'Airport', icon: '✈️', color: 'bg-sky-600', group: 'transport' },
  { value: 'train', label: 'Vlak', labelEn: 'Train', icon: '🚂', color: 'bg-slate-600', group: 'transport' },
  { value: 'rental_car', label: 'Rent-a-car', labelEn: 'Car Rental', icon: '🚗', color: 'bg-indigo-600', group: 'transport' },

  // Services & Emergency
  { value: 'pharmacy', label: 'Ljekarna', labelEn: 'Pharmacy', icon: '💊', color: 'bg-red-500', group: 'services' },
  { value: 'hospital', label: 'Bolnica', labelEn: 'Hospital', icon: '🏥', color: 'bg-red-600', group: 'services' },
  { value: 'police', label: 'Policija', labelEn: 'Police', icon: '👮', color: 'bg-blue-800', group: 'services' },
  { value: 'vet', label: 'Veterinar', labelEn: 'Veterinarian', icon: '🐕', color: 'bg-amber-500', group: 'services' },
  { value: 'shop', label: 'Trgovina', labelEn: 'Shop', icon: '🛒', color: 'bg-emerald-500', group: 'services' },
  { value: 'atm', label: 'Bankomat', labelEn: 'ATM', icon: '🏧', color: 'bg-green-700', group: 'services' },
  { value: 'gas_station', label: 'Benzinska', labelEn: 'Gas Station', icon: '⛽', color: 'bg-orange-600', group: 'services' },
  { value: 'market', label: 'Tržnica', labelEn: 'Market', icon: '🥬', color: 'bg-green-500', group: 'services' },

  // Events
  { value: 'event', label: 'Događaj', labelEn: 'Event', icon: '🎉', color: 'bg-purple-600', group: 'events' },
  { value: 'festival', label: 'Festival', labelEn: 'Festival', icon: '🎪', color: 'bg-pink-600', group: 'events' },
  { value: 'concert', label: 'Koncert', labelEn: 'Concert', icon: '🎵', color: 'bg-violet-500', group: 'events' },

  // Other
  { value: 'other', label: 'Ostalo', labelEn: 'Other', icon: '📍', color: 'bg-gray-500', group: 'other' },
];

// Group categories for UI display
export const CATEGORY_GROUPS = {
  food: { label: 'Hrana i piće', labelEn: 'Food & Drink', icon: '🍽️' },
  nature: { label: 'Priroda i znamenitosti', labelEn: 'Nature & Sights', icon: '🏞️' },
  activities: { label: 'Aktivnosti i sport', labelEn: 'Activities & Sports', icon: '🏄' },
  transport: { label: 'Prijevoz', labelEn: 'Transport', icon: '🚌' },
  services: { label: 'Usluge i hitno', labelEn: 'Services & Emergency', icon: '🏥' },
  events: { label: 'Događanja', labelEn: 'Events', icon: '🎉' },
  other: { label: 'Ostalo', labelEn: 'Other', icon: '📍' },
};

export function getCategoryInfo(category: RecommendationCategory) {
  return RECOMMENDATION_CATEGORIES.find((c) => c.value === category) || RECOMMENDATION_CATEGORIES[RECOMMENDATION_CATEGORIES.length - 1];
}

// ============================================
// FETCH RECOMMENDATIONS
// ============================================
export function useRecommendations(propertyId: string | undefined) {
  return useQuery({
    queryKey: ['recommendations', propertyId],
    queryFn: async () => {
      if (!propertyId) return [];

      const { data, error } = await getSupabase()
        .from('local_recommendations')
        .select('*')
        .eq('property_id', propertyId)
        .is('deleted_at', null) // trashed places must not reach guests
        .order('display_order', { ascending: true })
        .order('created_at', { ascending: false });

      if (error) throw error;
      return data as LocalRecommendation[];
    },
    enabled: !!propertyId,
  });
}

// Fetch recommendations by category for guest view
export function useRecommendationsByCategory(propertyId: string | undefined) {
  const { data: recommendations, ...rest } = useRecommendations(propertyId);

  // Group by category
  const byCategory = recommendations?.reduce(
    (acc, rec) => {
      if (!acc[rec.category]) {
        acc[rec.category] = [];
      }
      acc[rec.category].push(rec);
      return acc;
    },
    {} as Record<RecommendationCategory, LocalRecommendation[]>
  );

  return { byCategory, recommendations, ...rest };
}

// ============================================
// CREATE RECOMMENDATION
// ============================================
export function useCreateRecommendation() {
  const { user } = useUser();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (input: CreateRecommendationInput) => {
      if (!user?.id) throw new Error('User not authenticated');

      // Sanitize inputs
      const sanitizedData = {
        ...input,
        landlord_id: user.id,
        name: DOMPurify.sanitize(input.name),
        description: input.description ? DOMPurify.sanitize(input.description) : null,
        address: input.address ? DOMPurify.sanitize(input.address) : null,
        city: input.city ? DOMPurify.sanitize(input.city) : null,
        personal_note: input.personal_note ? DOMPurify.sanitize(input.personal_note) : null,
        phone: input.phone ? DOMPurify.sanitize(input.phone) : null,
        website: input.website ? DOMPurify.sanitize(input.website) : null,
        google_maps_url: input.google_maps_url ? DOMPurify.sanitize(input.google_maps_url) : null,
        subcategory: input.subcategory ? DOMPurify.sanitize(input.subcategory) : null,
      };

      const { data, error } = await getSupabase()
        .from('local_recommendations')
        .insert(sanitizedData)
        .select()
        .single();

      if (error) throw error;
      return data as LocalRecommendation;
    },
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: ['recommendations', variables.property_id] });
    },
  });
}

// ============================================
// UPDATE RECOMMENDATION
// ============================================
export function useUpdateRecommendation() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({
      id,
      propertyId,
      ...updates
    }: Partial<LocalRecommendation> & { id: string; propertyId: string }) => {
      // Sanitize text inputs
      const sanitizedUpdates: Partial<LocalRecommendation> = { ...updates };
      if (updates.name) sanitizedUpdates.name = DOMPurify.sanitize(updates.name);
      if (updates.description) sanitizedUpdates.description = DOMPurify.sanitize(updates.description);
      if (updates.address) sanitizedUpdates.address = DOMPurify.sanitize(updates.address);
      if (updates.city) sanitizedUpdates.city = DOMPurify.sanitize(updates.city);
      if (updates.personal_note) sanitizedUpdates.personal_note = DOMPurify.sanitize(updates.personal_note);
      if (updates.phone) sanitizedUpdates.phone = DOMPurify.sanitize(updates.phone);
      if (updates.website) sanitizedUpdates.website = DOMPurify.sanitize(updates.website);
      if (updates.subcategory) sanitizedUpdates.subcategory = DOMPurify.sanitize(updates.subcategory);

      const { data, error } = await getSupabase()
        .from('local_recommendations')
        .update(sanitizedUpdates)
        .eq('id', id)
        .select()
        .single();

      if (error) throw error;
      return { data, propertyId };
    },
    onSuccess: (result) => {
      queryClient.invalidateQueries({ queryKey: ['recommendations', result.propertyId] });
    },
  });
}

// ============================================
// DELETE RECOMMENDATION
// ============================================
export function useDeleteRecommendation() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ id, propertyId }: { id: string; propertyId: string }) => {
      const { error } = await getSupabase().from('local_recommendations').delete().eq('id', id);

      if (error) throw error;
      return { propertyId };
    },
    onSuccess: (result) => {
      queryClient.invalidateQueries({ queryKey: ['recommendations', result.propertyId] });
    },
  });
}

// ============================================
// TOGGLE ACTIVE STATUS
// ============================================
export function useToggleRecommendationActive() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({
      id,
      propertyId,
      isActive,
    }: {
      id: string;
      propertyId: string;
      isActive: boolean;
    }) => {
      const { error } = await getSupabase()
        .from('local_recommendations')
        .update({ is_active: isActive })
        .eq('id', id);

      if (error) throw error;
      return { propertyId };
    },
    onSuccess: (result) => {
      queryClient.invalidateQueries({ queryKey: ['recommendations', result.propertyId] });
    },
  });
}

// ============================================
// REORDER RECOMMENDATIONS
// ============================================
export function useReorderRecommendations() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({
      propertyId,
      orderedIds,
    }: {
      propertyId: string;
      orderedIds: string[];
    }) => {
      // Update display_order for each recommendation
      const updates = orderedIds.map((id, index) =>
        getSupabase()
          .from('local_recommendations')
          .update({ display_order: index })
          .eq('id', id)
      );

      await Promise.all(updates);
      return { propertyId };
    },
    onSuccess: (result) => {
      queryClient.invalidateQueries({ queryKey: ['recommendations', result.propertyId] });
    },
  });
}
